
import React, { useState, useEffect, useMemo, useRef } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation, useNavigate, Navigate } from 'react-router-dom';
import { translations, Language, LANGUAGE_NAMES, LANGUAGES, WELLNESS_SERVICES } from './translations';
import { Category, Expedition } from './types';
import { PayPalScriptProvider } from '@paypal/react-paypal-js';
import { supabase } from './lib/supabase';
import { updateSEO, getExpeditionSEO, getHomeSEO } from './lib/seo';
import { sortExpeditions } from './lib/expeditionOrder';
import { getCleanPath, getLocalizedPath, extractLangFromPath } from './lib/i18n';
import { Loader2 } from 'lucide-react';

// Components
import Navbar from './components/Navbar';
import Footer from './components/Footer';

// Pages
import Home from './pages/Home';
import HomeA from './pages/HomeA';
import HomeB from './pages/HomeB';
import Expeditions from './pages/Expeditions';
import About from './pages/About';
import Certifications from './pages/Certifications';
import Blog from './pages/Blog';
import BlogPost from './pages/BlogPost';
import ExpeditionDetail from './pages/ExpeditionDetail';
import Terms from './pages/Terms';
import Policies from './pages/Policies';
import Registration from './pages/Registration';
import NotFound from './pages/NotFound';
import Success from './pages/Success';
import AdminLayout from './pages/Admin/AdminLayout';
import Dashboard from './pages/Admin/Dashboard';
import ExpeditionsManager from './pages/Admin/ExpeditionsManager';
import CalendarManager from './pages/Admin/CalendarManager';
import TeamManager from './pages/Admin/TeamManager';
import BookingsManager from './pages/Admin/BookingsManager';
import UsersManager from './pages/Admin/UsersManager';
import HomeEditor from './pages/Admin/HomeEditor';
import ReviewsManager from './pages/Admin/ReviewsManager';
import ReviewPage from './pages/ReviewPage';
import Documents from './pages/Documents';
import Login from './pages/Login';
import ProtectedRoute from './components/ProtectedRoute';



declare global {
  interface Window {
    gtag: (command: string, id: string, config?: any) => void;
  }
}

/* --- LANGUAGE DETECTION --- */
function detectLanguage(): Language {
  if (typeof window !== 'undefined') {
    const { lang, hasPrefix } = extractLangFromPath(window.location.pathname);
    if (hasPrefix && lang) return lang;
  }
  const browserLang = (navigator.language || 'es').split('-')[0].toLowerCase() as Language;
  return LANGUAGES.includes(browserLang) ? browserLang : 'es';
}

/* --- EXPEDITION HELPER --- */
function getExpField<T>(exp: any, field: string, lang: Language): T {
  if (!exp) return undefined as any;
  const isWellness = exp.id === 'wellness' || exp.slug === 'wellness-day' || exp.id === 'wellness-day';
  if (field === 'detailed_services' && isWellness && WELLNESS_SERVICES[lang]) {
    return WELLNESS_SERVICES[lang] as unknown as T;
  }
  if (field === 'services' && isWellness && WELLNESS_SERVICES[lang]) {
    return WELLNESS_SERVICES[lang].map(s => s.name) as unknown as T;
  }
  const valLang = exp[`${field}_${lang}`];
  if (valLang !== undefined && valLang !== null && valLang !== '') return valLang as T;
  const valEn = exp[`${field}_en`];
  if (valEn !== undefined && valEn !== null && valEn !== '') return valEn as T;
  return exp[`${field}_es`] as T;
}

const TRUST_ITEMS = [
  { key: 'trust_angm', icon: 'M12 2L2 12l10 10 10-10L12 2z' },
  { key: 'trust_wfr', icon: 'M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-2 10h-4v4h-2v-4H7v-2h4V7h2v4h4v2z' },
  { key: 'trust_gps', icon: 'M12 2c-4.41 0-8 3.59-8 8 0 5.25 8 13 8 13s8-7.75 8-13c0-4.41-3.59-8-8-8zm0 11c-1.66 0-3-1.34-3-3s1.34-3 3-3 3 1.34 3 3-1.34 3-3 3z' },
  { key: 'trust_lang', icon: 'M12.87 15.07l-2.54-2.51.03-.03c1.74-1.94 2.98-4.17 3.71-6.53H17V4h-7V2H8v2H1v1.99h11.17C11.5 7.92 10.44 9.75 9 11.35 8.07 10.32 7.3 9.19 6.69 8h-2c.73 1.63 1.73 3.17 2.98 4.56l-5.09 5.02L4 19l5-5 3.11 3.11.76-1.04z' },
  { key: 'trust_groups', icon: 'M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5s-3 1.34-3 3 1.34 3 3 3zm-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5c0-2.33-4.67-3.5-7-3.5z' },
  { key: 'trust_ndr', icon: 'M12 2L4.5 20.29l.71.71L12 18l6.79 3 .71-.71L12 2z' }
];

const AppContent: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const { lang: urlLang, cleanPath } = useMemo(() => {
    return extractLangFromPath(location.pathname);
  }, [location.pathname]);

  const [lang, setLang] = useState<Language>(() => {
    return urlLang || detectLanguage();
  });

  // Sync lang state if user navigates directly to or from a language path
  useEffect(() => {
    if (urlLang && lang !== urlLang) {
      setLang(urlLang);
    }
  }, [urlLang]);

  const [filter, setFilter] = useState<Category>('all');
  const [langOpen, setLangOpen] = useState(false);
  const langRef = useRef<HTMLDivElement>(null);
  const CACHE_KEY = 'pc_supabase_expeditions_v7';

  const [expeditions, setExpeditions] = useState<Expedition[]>(() => {
    try {
      localStorage.removeItem('pc_supabase_expeditions');
      localStorage.removeItem('pc_expeditions_cache');
      localStorage.removeItem('pc_supabase_expeditions_v3');
      localStorage.removeItem('pc_supabase_expeditions_v4');
      localStorage.removeItem('pc_supabase_expeditions_v5');
      localStorage.removeItem('pc_supabase_expeditions_v6');
      const cached = localStorage.getItem(CACHE_KEY);
      return cached ? sortExpeditions(JSON.parse(cached)) : [];
    } catch {
      return [];
    }
  });
  const [loading, setLoading] = useState(true);
  const t = translations[lang] || translations.es;
  const isHome = cleanPath === '/' || cleanPath === '/homea' || cleanPath === '/homeb';
  const isAuthPage = cleanPath === '/login';
  
  // Use a more robust check that works even if the router is still resolving
  const isAdmin = location.pathname.includes('/admin') || window.location.pathname.includes('/admin');

  const [homeSections, setHomeSections] = useState<any[]>([]);

  // Fetch expeditions & home sections dynamically from Supabase
  useEffect(() => {
    const fetchExpeditions = async () => {
      try {
        const { data, error } = await supabase
          .from('expeditions')
          .select('*')
          .order('created_at', { ascending: true });
        if (error) throw error;
        if (data && data.length > 0) {
          const sorted = sortExpeditions(data);
          setExpeditions(sorted);
          try {
            localStorage.setItem(CACHE_KEY, JSON.stringify(sorted));
          } catch (e) {
            console.warn('Cache error:', e);
          }
        }
      } catch (err) {
        console.error('Failed to fetch expeditions from Supabase:', err);
      } finally {
        setLoading(false);
      }
    };

    const fetchHomeSections = async () => {
      try {
        const { data, error } = await supabase
          .from('home_sections')
          .select('*');
        if (error) throw error;
        if (data && data.length > 0) {
          setHomeSections(data);
        }
      } catch (err) {
        console.error('Failed to fetch home sections from Supabase:', err);
      }
    };

    fetchExpeditions();
    fetchHomeSections();
  }, []);

  // Scroll to top & Google Analytics tracking on route change + SEO update
  useEffect(() => {
    window.scrollTo(0, 0);

    const path = cleanPath;
    const siteUrl = 'https://fisuraexpeditions.com';
    const canonicalUrl = lang === 'es'
      ? `${siteUrl}${cleanPath === '/' ? '/' : cleanPath}`
      : `${siteUrl}/${lang}${cleanPath === '/' ? '' : cleanPath}`;

    let seoTitle = t.home_seo_title || 'Fisura Expeditions | Tours de trekking y escalada en Patagonia';
    let seoDesc = t.home_seo_desc || (lang === 'en'
      ? 'Official tour operator in Villa Cerro Castillo and Aysén. Specialists in trekking tours to Laguna Cerro Castillo, Las Horquetas traverse, and rock climbing in Patagonia.'
      : 'Operador turístico oficial en Villa Cerro Castillo y Aysén. Especialistas en tours de trekking a Laguna Cerro Castillo, Travesía Las Horquetas y escalada técnica en Patagonia.');
    let seoKeywords = ['Fisura Expeditions', 'Cerro Castillo', 'Aysén', 'Guías de Montaña', 'Trekking', 'Escalada'];
    let seoImage = 'https://iuixlsqrlfkfnvnckgij.supabase.co/storage/v1/object/public/expeditions/Laguna%20Cerro%20Castillo/trekking-laguna-cerro-castillo-turquesa.jpg';
    let seoSchema: object | null = null;
    let isExpedition = false;

    if (path.startsWith('/expeditions/')) {
      const slug = path.split('/').pop();
      const exp = expeditions.find(e => e.slug === slug);
      if (exp) {
        isExpedition = true;
        const currentExpUrl = lang === 'es'
          ? `https://fisuraexpeditions.com/expeditions/${exp.slug}`
          : `https://fisuraexpeditions.com/${lang}/expeditions/${exp.slug}`;
        const expSEO = getExpeditionSEO(exp, lang, currentExpUrl);
        seoTitle = expSEO.title;
        seoDesc = expSEO.description;
        seoKeywords = expSEO.keywords || [];
        seoImage = expSEO.image || seoImage;
        seoSchema = expSEO.schemaData || null;
      }
    } else {
      if (path === '/') {
        const homeSEO = getHomeSEO(lang, canonicalUrl, t);
        seoTitle = homeSEO.title;
        seoDesc = homeSEO.description;
        seoKeywords = homeSEO.keywords || seoKeywords;
        seoSchema = homeSEO.schemaData || null;
      } else if (path === '/homea') {
        seoTitle = `Propuesta A: Parallax Geológico | Fisura Expeditions`;
      } else if (path === '/homeb') {
        seoTitle = `Propuesta B: Línea Conductora | Fisura Expeditions`;
      } else if (path === '/expeditions') {
        seoTitle = `${t.nav_expeditions} | Fisura Expeditions`;
        seoDesc = lang === 'en' 
          ? 'Discover our mountain expeditions, rock climbing, and trekking in Villa Cerro Castillo and Aysén.'
          : 'Descubre nuestras expediciones de montaña, escalada en roca y trekkings en Villa Cerro Castillo y Aysén.';
      } else if (path === '/about') {
        seoTitle = `${t.nav_about} | Fisura Expeditions`;
        seoDesc = lang === 'en'
          ? 'Meet our certified mountain guides and instructors operating in Villa Cerro Castillo.'
          : 'Conoce a nuestros guías de montaña certificados e instructores en Villa Cerro Castillo.';
      } else if (path === '/certificaciones' || path === '/certifications' || path === '/certificacoes' || path === '/sustentabilidad' || path === '/sustainability' || path === '/compromiso-sustentable') {
        seoTitle = lang === 'en'
          ? 'Sustainability & Accreditations | Fisura Expeditions'
          : lang === 'pt'
          ? 'Compromisso Sustentável e Certificações | Fisura Expeditions'
          : 'Compromiso Sustentable y Certificaciones | Fisura Expeditions';
        seoDesc = lang === 'en'
          ? 'Official SERNATUR registered tour operator, Sustainable Tourism commitment, roadmap to Sello S, and certified mountain safety in Aysén.'
          : 'Compromiso de Turismo Sustentable con SERNATUR, ruta hacia el Sello S, registro oficial de turismo aventura y seguridad en Aysén.';
      } else if (path === '/terms') {
        seoTitle = `${t.terms_title} | Fisura Expeditions`;
      } else if (path === '/policies') {
        seoTitle = `Políticas y Condiciones | Fisura Expeditions`;
      } else if (path === '/registro') {
        seoTitle = lang === 'en' ? 'Participant Registration | Fisura Expeditions' : 'Registro de Participantes | Fisura Expeditions';
      } else if (path === '/booking-success' || path === '/success') {
        seoTitle = lang === 'en' ? 'Booking Confirmed | Fisura Expeditions' : 'Confirmación de Reserva | Fisura Expeditions';
      } else if (path.startsWith('/evaluar/')) {
        seoTitle = lang === 'en' ? 'Experience Survey | Fisura Expeditions' : 'Encuesta de Experiencia | Fisura Expeditions';
      } else if (path === '/documents') {
        seoTitle = lang === 'en' ? 'Expedition Documents | Fisura Expeditions' : 'Documentos de Expedición | Fisura Expeditions';
      } else if (path === '/test') {
        seoTitle = `Test de Pago | Fisura Expeditions`;
      }
    }

    const alternates = [
      ...LANGUAGES.map(l => ({
        lang: l,
        href: l === 'es'
          ? `${siteUrl}${cleanPath === '/' ? '/' : cleanPath}`
          : `${siteUrl}/${l}${cleanPath === '/' ? '' : cleanPath}`
      })),
      { lang: 'x-default', href: `${siteUrl}${cleanPath === '/' ? '/' : cleanPath}` }
    ];

    updateSEO({
      title: seoTitle,
      description: seoDesc,
      keywords: seoKeywords,
      image: seoImage,
      url: canonicalUrl,
      type: isExpedition ? 'product' : 'website',
      schemaData: seoSchema,
      currentLang: lang,
      alternates
    });

    // Report to GA4
    if (typeof window.gtag === 'function') {
      window.gtag('config', 'G-4V84WY9TYC', {
        page_path: location.pathname,
        page_title: seoTitle
      });
    }
  }, [cleanPath, lang, t, expeditions]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (langRef.current && !langRef.current.contains(e.target as Node)) {
        setLangOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Sync internal filter with URL parameter
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const urlFilter = params.get('filter') as Category;
    if (urlFilter && ['all', 'trekking', 'climbing', 'water', 'wellness', 'fishing'].includes(urlFilter)) {
      setFilter(urlFilter);
    } else if (!urlFilter && cleanPath === '/expeditions') {
      setFilter('all');
    }
  }, [location.search, cleanPath]);

  const handleFilterChange = (newFilter: Category) => {
    setFilter(newFilter);
    const params = new URLSearchParams(location.search);
    if (newFilter === 'all') {
      params.delete('filter');
    } else {
      params.set('filter', newFilter);
    }
    const newSearch = params.toString();
    navigate({
      pathname: getLocalizedPath('/expeditions', lang),
      search: newSearch ? `?${newSearch}` : ''
    }, { replace: true });
  };

  const filteredExpeditions = useMemo(() => {
    // Exclude hidden expeditions
    const visibleExpeditions = expeditions.filter(e => e.is_visible !== false);
    // Exclude test and wellness from the 'all' view to keep wellness as a separate pillar
    const mainExpeditions = visibleExpeditions.filter(e => e.category !== 'test' && e.category !== 'wellness');
    if (filter === 'all') return mainExpeditions;
    // For specific categories (including 'wellness'), show only those
    return visibleExpeditions.filter(e => e.category === filter);
  }, [filter, expeditions]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex flex-col items-center justify-center gap-6">
        <style dangerouslySetInnerHTML={{__html: `
          @keyframes logoShimmer {
            0% { left: -150%; }
            100% { left: 150%; }
          }
          .animate-logo-shimmer {
            animation: logoShimmer 2.2s cubic-bezier(0.4, 0, 0.2, 1) infinite;
          }
        `}} />
        <div className="relative w-80 h-32 flex items-center justify-center overflow-hidden">
          {/* Base mountain in soft opacity */}
          <img 
            src="/logo-fisura-white.png" 
            className="w-full h-full object-contain opacity-10" 
            alt="Fisura Expeditions"
          />
          {/* Shimmer mask mountain */}
          <div 
            className="absolute inset-0 w-full h-full"
            style={{
              maskImage: "url('/logo-fisura-white.png')",
              WebkitMaskImage: "url('/logo-fisura-white.png')",
              maskSize: "contain",
              WebkitMaskSize: "contain",
              maskRepeat: "no-repeat",
              WebkitMaskRepeat: "no-repeat",
              maskPosition: "center",
              WebkitMaskPosition: "center",
            }}
          >
            {/* The light/bar passing from left to right */}
            <div 
              className="absolute inset-y-0 w-[40%] bg-gradient-to-r from-transparent via-[#00d4ff] to-transparent animate-logo-shimmer"
              style={{
                transform: 'skewX(-25deg)',
              }}
            />
          </div>
        </div>
        <p className="text-[9px] text-gray-500 uppercase tracking-[0.4em] font-bold animate-pulse">Cargando...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-[#f2f2f2] selection:bg-[#00d4ff] selection:text-black overflow-x-hidden w-full">
      {(!isAdmin && !isAuthPage) && (
        <Navbar
          lang={lang}
          setLang={setLang}
          langOpen={langOpen}
          setLangOpen={setLangOpen}
          langRef={langRef}
          t={t}
          LANGUAGES={LANGUAGES}
          expeditions={expeditions}
          getExpField={getExpField}
        />
      )}

      <main className={(isHome || isAdmin || isAuthPage) ? '' : 'pt-[130px] md:pt-20'}>


        <Routes>
          {/* Default (Spanish) Routes */}
          <Route path="/" element={<Home t={t} lang={lang} expeditions={expeditions} homeSections={homeSections} getExpField={getExpField} />} />
          <Route path="/homea" element={<HomeA t={t} lang={lang} expeditions={expeditions} homeSections={homeSections} />} />
          <Route path="/homeb" element={<HomeB t={t} lang={lang} expeditions={expeditions} homeSections={homeSections} />} />
          <Route path="/test" element={
            <ExpeditionDetail
              t={t}
              lang={lang}
              getExpField={getExpField}
              overrideSlug="test"
              expeditions={expeditions}
            />
          } />
          <Route path="/expeditions" element={
            <Expeditions
              t={t}
              lang={lang}
              filter={filter}
              setFilter={handleFilterChange}
              expeditions={filteredExpeditions}
              getExpField={getExpField}
            />
          } />
          <Route path="/about" element={<About t={t} TRUST_ITEMS={TRUST_ITEMS} lang={lang} homeSections={homeSections} />} />
          <Route path="/certificaciones" element={<Certifications t={t} lang={lang} />} />
          <Route path="/certifications" element={<Certifications t={t} lang={lang} />} />
          <Route path="/certificacoes" element={<Certifications t={t} lang={lang} />} />
          <Route path="/sustentabilidad" element={<Certifications t={t} lang={lang} />} />
          <Route path="/sustainability" element={<Certifications t={t} lang={lang} />} />
          <Route path="/compromiso-sustentable" element={<Certifications t={t} lang={lang} />} />
          <Route path="/wellness" element={<Navigate to="/expeditions/wellness-day" replace />} />
          <Route path="/wellness-day" element={<Navigate to="/expeditions/wellness-day" replace />} />
          <Route path="/blog" element={<Blog t={t} lang={lang} />} />
          <Route path="/blog/:slug" element={<BlogPost t={t} lang={lang} />} />
          <Route path="/expeditions/:slug" element={
            <ExpeditionDetail
              key={location.pathname}
              t={t}
              lang={lang}
              getExpField={getExpField}
              expeditions={expeditions}
            />
          } />
          <Route path="/terms" element={<Terms />} />
          <Route path="/policies" element={<Policies />} />
          <Route path="/registro" element={<Registration t={t} lang={lang} expeditions={expeditions} />} />
          <Route path="/booking-success" element={<Success t={t} lang={lang} />} />
          <Route path="/success" element={<Success t={t} lang={lang} />} />
          <Route path="/evaluar/:bookingId" element={<ReviewPage expeditions={expeditions} />} />
          <Route path="/documents" element={<Documents />} />
          <Route path="/login" element={<Login />} />

          {/* Localized Language Routes (e.g. /en, /en/expeditions, /pt, /fr, etc.) */}
          {LANGUAGES.filter(l => l !== 'es').map(langCode => {
            const langT = translations[langCode] || translations.en;
            return (
              <Route key={langCode} path={`/${langCode}`}>
                <Route index element={<Home t={langT} lang={langCode} expeditions={expeditions} homeSections={homeSections} getExpField={getExpField} />} />
                <Route path="expeditions" element={
                  <Expeditions
                    t={langT}
                    lang={langCode}
                    filter={filter}
                    setFilter={handleFilterChange}
                    expeditions={filteredExpeditions}
                    getExpField={getExpField}
                  />
                } />
                <Route path="about" element={<About t={langT} TRUST_ITEMS={TRUST_ITEMS} lang={langCode} homeSections={homeSections} />} />
                <Route path="certificaciones" element={<Certifications t={langT} lang={langCode} />} />
                <Route path="certifications" element={<Certifications t={langT} lang={langCode} />} />
                <Route path="certificacoes" element={<Certifications t={langT} lang={langCode} />} />
                <Route path="sustentabilidad" element={<Certifications t={langT} lang={langCode} />} />
                <Route path="sustainability" element={<Certifications t={langT} lang={langCode} />} />
                <Route path="compromiso-sustentable" element={<Certifications t={langT} lang={langCode} />} />
                <Route path="wellness" element={<Navigate to={`/${langCode}/expeditions/wellness-day`} replace />} />
                <Route path="wellness-day" element={<Navigate to={`/${langCode}/expeditions/wellness-day`} replace />} />
                <Route path="blog" element={<Blog t={langT} lang={langCode} />} />
                <Route path="blog/:slug" element={<BlogPost t={langT} lang={langCode} />} />
                <Route path="expeditions/:slug" element={
                  <ExpeditionDetail
                    key={location.pathname}
                    t={langT}
                    lang={langCode}
                    getExpField={getExpField}
                    expeditions={expeditions}
                  />
                } />
                <Route path="terms" element={<Terms />} />
                <Route path="policies" element={<Policies />} />
                <Route path="registro" element={<Registration t={langT} lang={langCode} expeditions={expeditions} />} />
                <Route path="booking-success" element={<Success t={langT} lang={langCode} />} />
                <Route path="success" element={<Success t={langT} lang={langCode} />} />
                <Route path="evaluar/:bookingId" element={<ReviewPage expeditions={expeditions} />} />
                <Route path="documents" element={<Documents />} />
              </Route>
            );
          })}
          
          {/* Admin Routes */}
          <Route element={<ProtectedRoute />}>
            <Route path="/admin" element={<AdminLayout expeditions={expeditions} setExpeditions={setExpeditions} />}>
              <Route index element={<Dashboard />} />
              <Route path="web" element={<HomeEditor />} />
              <Route path="expeditions" element={<ExpeditionsManager />} />
              <Route path="calendar" element={<CalendarManager />} />
              <Route path="team" element={<TeamManager />} />
              <Route path="bookings" element={<BookingsManager />} />
              <Route path="users" element={<UsersManager />} />
              <Route path="reviews" element={<ReviewsManager />} />
            </Route>
          </Route>

          <Route path="*" element={<NotFound t={t} />} />

        </Routes>
      </main>

      {!isAdmin && !isAuthPage && <Footer t={t} homeSections={homeSections} lang={lang} />}

      {!isAdmin && !isAuthPage && (
        <a
          href="https://wa.me/56967273815"
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => {
            console.log("🔍 WhatsApp floating button clicked! Sending GA4 event click_whatsapp...");
            if (typeof window.gtag === 'function') {
              window.gtag('event', 'click_whatsapp', {
                location: 'floating_button'
              });
            }
          }}
          className="fixed bottom-32 md:bottom-12 right-6 z-[100] bg-[#25D366] text-white p-4 rounded-full shadow-2xl hover:scale-110 active:scale-95 transition-all duration-300 flex items-center justify-center group"
          aria-label="Chat on WhatsApp"
        >
          <svg className="w-7 h-7 fill-current" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
          </svg>
          <div className="absolute bottom-full mb-4 right-0 bg-[#1a1512] border border-rock px-4 py-2 rounded text-[10px] font-bold uppercase tracking-[0.2em] text-[#25D366] opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none shadow-2xl">
            {t.whatsapp_chat}
          </div>
        </a>
      )}




    </div>

  );
};

const paypalOptions = {
  clientId: (import.meta as any).env.VITE_PAYPAL_CLIENT_ID || "sb",
  currency: "USD"
};

const App: React.FC = () => {
  return (
    <PayPalScriptProvider options={paypalOptions}>
      <Router>
        <AppContent />
      </Router>
    </PayPalScriptProvider>
  );
};

export default App;
